import React, { useEffect, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import styled from "@emotion/styled";
import axios from "axios";
import { DialogTitle, DialogActions, DialogContent } from "@mui/material";
import { Button, Dialog, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import FolderIcon from "@mui/icons-material/Folder";
import LockIcon from "@mui/icons-material/Lock";
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useTheme } from "../../../../components/hooks/useTheme";
import useAuth from "../../../../components/hooks/useAuth";
import baseURL from "../../../../configs/api";
import colorConfigs from "../../../../configs/colorConfigs";
import PleaseWaitPopup from "../../../PleaseWaitPopup";
import { CustomDataGrid } from "../../../../components/common/CustomDataGrid";
import EmailIcon from '@mui/icons-material/Email';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { SendEmailVehicleFile } from "./SendEmailVehicleFile";
import { FilesPreviewPopup } from "./FilesPreviewPopup";
import useDelete from "../../../../components/hooks/useDelete";
import useConfirm from "../../../../components/hooks/useConfirm";
import Cookies from "js-cookie";
import useTenant from "../../../../components/hooks/useTenant";
import moment from "moment";
import useGeneral from "../../../../components/hooks/useGeneral";

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

type Props = {
    idNumber: any;
};

type VehicleChecklistRow = {
    id: number;
    fileName: string;
    filePath?: string;
    fileSize: number;
    uploadedDate: string;
    tags: string;
    description: string;
    createdAt: string;
    createdBy: string;
};

export const VehicleFiles = ({
    idNumber
}: Props) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { themeStyles } = useTheme();
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const { dateFormat }: any = useGeneral();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const { setDeletePopup, setData }: any = useDelete();
    const [tableData, setTableData] = useState<VehicleChecklistRow[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [imagePopup, setImagePopup] = useState<boolean>(false);
    const [pleaseWaitPopup, setPleaseWaitPopup] = useState<boolean>(false);
    const [isFilesRefresh, setIsFileRefresh] = useState<boolean>(true);
    const [openSendEmailPopup, setOpenSendEmailPopup] = useState(false);
    const [fileNameForEmail, setFileNameForEmail] = useState<string>("");
    const [previewFilePath, setPreviewFilePath] = useState<string>("");
    const [selectedRow, setSelectedRow] = useState<VehicleChecklistRow | null>(null);
    const [openFolderDialog, setOpenFolderDialog] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    const [folders, setFolders] = useState<any[]>([]);
    const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
    const [newFolderName, setNewFolderName] = useState("");
    const [editFolderId, setEditFolderId] = useState<number | null>(null);
    const [editFolderName, setEditFolderName] = useState("");
    const [newFolderIsLocked, setNewFolderIsLocked] = useState(false);
    const [editFolderIsLocked, setEditFolderIsLocked] = useState(false);
    const [totalLength, setTotalLength] = useState<number>(0);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const columns: GridColDef[] = [
        {
            field: "fileName",
            headerClassName: themeStyles.table.headerBg,
            headerName: "File",
            align: "left",
            editable: false,
            sortable: false,
            flex: 3,
            renderCell: (params) => {
                return (
                    <span
                        onClick={() => {
                            const selected = params.row;
                            setPreviewFilePath(selected.fileName);
                            setSelectedRow(selected);

                            setTimeout(() => setImagePopup(true), 0);
                        }}
                        style={{ color: "var(--app-link-color)" }}
                        className="underline cursor-pointer"
                    >
                        {params?.value}
                    </span>
                );
            },
        },
        {
            field: "tags",
            headerName: "Tags",
            headerClassName: themeStyles.table.headerBg,
            editable: false,
            sortable: false,
            align: "left",
            flex: 1,
        },
        {
            field: "createdBy",
            headerName: "Author",
            headerClassName: themeStyles.table.headerBg,
            editable: false,
            sortable: false,
            align: "left",
            flex: 1,
        },
        {
            field: "fileSize",
            headerName: "Size",
            headerClassName: themeStyles.table.headerBg,
            editable: false,
            sortable: false,
            flex: 1,
            align: "left",
            renderCell: (params) => (
                params.value === 0 ? "-" : `${(params.value / 1024).toFixed(2)} KB`
            )
        },
        {
            field: "uploadedDate",
            headerName: "Date",
            editable: false,
            headerClassName: themeStyles.table.headerBg,
            sortable: false,
            flex: 1,
            align: "left"
        },
        {
            field: "actions",
            headerName: "Actions",
            headerClassName: themeStyles.table.headerBg,
            flex: 2,
            align: "center",
            sortable: false,
            editable: false,
            renderCell: (params) => {
                const row = params.row as VehicleChecklistRow;
                return (
                    <div className="flex gap-2 items-center">
                        <EmailIcon
                            className="cursor-pointer text-[#1976d2]"
                            titleAccess="Send Email"
                            onClick={() => {
                                setFileNameForEmail(row.fileName || "");
                                setOpenSendEmailPopup(true);
                            }}
                        />
                        <DownloadIcon
                            className="cursor-pointer text-[#388e3c]"
                            titleAccess="Download"
                            onClick={() => {
                                handleDownload(row.fileName)
                            }
                            }
                        />
                        <DeleteIcon
                            className="cursor-pointer text-[#d32f2f]"
                            titleAccess="Delete File"
                            onClick={() => handleDelete(row)}
                        />
                        <DownloadIcon
                            className="text-[#f57c00] rotate-180 cursor-pointer"
                            titleAccess={row.filePath ? "Replace File" : "Upload File"}
                            onClick={() => {
                                document.getElementById(`upload-file-${row.id}`)?.click();
                            }}
                        />
                        <input
                            id={`upload-file-${row.id}`}
                            type="file"
                            accept=".png, .jpg, .jpeg, .pdf"
                            style={{ display: "none" }}
                            onChange={(e) => handleReplaceFile(e, row)}
                        />
                    </div>
                );
            },
        }
    ];

    const handleDownload = (fileName?: string) => {
        if (!fileName) return;
        const link = document.createElement("a");
        link.href = `${baseURL}vehicleFile/download?name=${fileName}`;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const handleUploadNewFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setPleaseWaitPopup(true);

        const formData = new FormData();
        formData.append("ImageFile", file);

        try {
            const res = await axios.post(`${baseURL}vehicleFile/upload`, formData, {
                headers: {
                    'X-Tenant-ID': tenantId,
                    "Content-Type": "multipart/form-data"
                },
                withCredentials: true,
            });

            const uploadedFileName = res.data;

            await saveFileOnFileUploadApi(uploadedFileName, file.size);

            await fetchVehicleFiles(paginationModel);

            handleTenantError(null);
        } catch (err) {
            handleTenantError(err);
        } finally {
            setPleaseWaitPopup(false);
        }
    };

    const saveFileOnFileUploadApi = async (fileName: string, fileSize: number) => {
        if (!fileName || !selectedFolderId) return;

        try {
            await axios.post(
                `${baseURL}vehicleFiles?userId=${auth?.id}`,
                JSON.stringify({
                    vehicleId: idNumber?.state,
                    folderId: selectedFolderId,
                    fileName,
                    tags: "",
                    fileSize,
                    uploadedDate: new Date(),
                    description: "",
                    createdAt: new Date(),
                    createdBy: auth?.givenname,
                }),
                {
                    headers: {
                        'X-Tenant-ID': tenantId,
                        "Content-Type": "application/json"
                    },
                    withCredentials: true,
                }
            );
            handleTenantError(null);
        } catch (error) {
            handleTenantError(error);
        }
    };

    const fetchVehicleFiles = async (newPaginationModel: any) => {
        if (Number(selectedFolderId) > 0) {
            try {
                setLoading(true);

                const { page, pageSize } = newPaginationModel;

                const res = await axios.get(
                    `${baseURL}vehicleFiles/GetVehicleFilesPagingByVehicleIdAndFolderId/${idNumber?.state},${selectedFolderId},${page},${pageSize}`,
                    {
                        headers: {
                            'X-Tenant-ID': tenantId,
                        },
                        withCredentials: true
                    }
                );

                const data = res.data;

                const result = data.vehicleFile.map((item: any) => ({
                    id: item.id,
                    fileName: item.fileName,
                    tags: item.tags,
                    fileSize: item.fileSize,
                    uploadedDate: moment(new Date(item.uploadedDate)).format(dateFormat + " hh:mm a"),
                    description: item.description,
                    createdAt: item.createdAt,
                    createdBy: item.createdBy,
                }));

                setTableData(result);
                setTotalLength(data.count);
                setLoading(false);
                handleTenantError(null);
            } catch (error) {
                handleTenantError(error);
                setTableData([]);
                setLoading(false);
            } finally {
                setIsFileRefresh(false);
            }
        } else {
            setTableData([]);
            setLoading(false);
            setIsFileRefresh(false);
        }
    };

    const handleReplaceFile = async (
        e: React.ChangeEvent<HTMLInputElement>,
        row: VehicleChecklistRow
    ) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setPleaseWaitPopup(true);

        const formData = new FormData();
        formData.append("ImageFile", file);

        try {
            const res = await axios.post(`${baseURL}vehicleFile/upload`, formData, {
                headers: {
                    'X-Tenant-ID': tenantId,
                    "Content-Type": "multipart/form-data"
                },
                withCredentials: true,
            });

            const uploadedFileName = res.data;

            const fileDetailsRes = await axios.get(
                `${baseURL}vehicleFiles/${row.id}`,
                {
                    headers: {
                        'X-Tenant-ID': tenantId,
                    },
                    withCredentials: true
                }
            );
            const fileData = fileDetailsRes.data;

            const payload = {
                ...fileData,
                fileName: uploadedFileName,
                fileSize: file.size,
                updatedAt: new Date(),
                updatedBy: auth?.givenname,
            };

            await axios.put(
                `${baseURL}vehicleFiles?userId=${auth?.id}`,
                JSON.stringify(payload),
                {
                    headers: {
                        'X-Tenant-ID': tenantId,
                        "Content-Type": "application/json"
                    },
                    withCredentials: true,
                }
            );

            await fetchVehicleFiles(paginationModel);
            handleTenantError(null);
        } catch (err) {
            handleTenantError(err);
        } finally {
            setPleaseWaitPopup(false);
        }
    };

    useEffect(() => {
        if (isFilesRefresh) {
            fetchVehicleFiles(paginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFilesRefresh]);

    const fetchFolders = async () => {
        try {
            const res = await axios.get(`${baseURL}vehicleFolder/GetVehicleFoldersByVehicle/${idNumber?.state}`, {
                headers: {
                    'X-Tenant-ID': tenantId,
                },
                withCredentials: true,
            });
            const foldersData = res.data || [];

            setFolders(foldersData);

            if (foldersData.length > 0) {
                const firstFolderId = foldersData[0].id;
                if (!selectedFolderId || selectedFolderId !== firstFolderId) {
                    setSelectedFolderId(firstFolderId);
                    setTimeout(() => setIsFileRefresh(true), 0);
                }
            } else {
                setSelectedFolderId(0);
                setTimeout(() => setIsFileRefresh(true), 0);
            }

            handleTenantError(null);
        } catch (err) {
            setSelectedFolderId(0);
            setTimeout(() => setIsFileRefresh(true), 0);
            handleTenantError(err);
        }
    };

    useEffect(() => {
        fetchFolders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idNumber?.state]);

    const addFolder = async () => {
        if (!newFolderName.trim()) return;
        setPleaseWaitPopup(true);
        try {
            await axios.post(`${baseURL}vehicleFolder`, {
                vehicleId: idNumber?.state,
                folderName: newFolderName.trim(),
                isLocked: newFolderIsLocked,
                createdBy: auth?.givenname,
            }, {
                headers: {
                    'X-Tenant-ID': tenantId,
                    "Content-Type": "application/json"
                },
                withCredentials: true,
            });
            setNewFolderName("");
            setNewFolderIsLocked(false);
            fetchFolders();
            handleTenantError(null);
        } catch (err) {
            handleTenantError(err);
        } finally {
            setPleaseWaitPopup(false);
        }
    };

    const updateFolder = async (folderId: number) => {
        if (!editFolderName.trim()) return;
        setPleaseWaitPopup(true);
        try {
            await axios.put(`${baseURL}vehicleFolder?userId=${auth?.id}`, {
                id: folderId,
                vehicleId: idNumber?.state,
                folderName: editFolderName.trim(),
                isLocked: editFolderIsLocked,
                updatedAt: new Date().toISOString(),
                updatedBy: auth?.givenname,
            }, {
                headers: {
                    'X-Tenant-ID': tenantId,
                    "Content-Type": "application/json"
                },
                withCredentials: true,
            });

            setEditFolderId(null);
            setEditFolderName("");
            setEditFolderIsLocked(false);
            fetchFolders();
            handleTenantError(null);
        } catch (err) {
            handleTenantError(err);
        } finally {
            setPleaseWaitPopup(false);
        }
    };

    const deleteFolder = (folderId: number) => {
        if (totalLength > 0) {
            setConfirmData({
                title: 'Warning',
                summary: `Please delete all files related to the ${folders?.find((folder) => folder?.id === selectedFolderId)?.folderName} folder first.`,
                confirmBtn: 'Ok',
                isConfirm: (callback: any) => {
                    callback();
                }
            });
            setConfirmPopup(true);
            return;
        }

        setData({
            title: "Delete Folder",
            summary: "Are you sure you want to delete this folder?",
            confirmBtn: "Yes",
            isDelete: async (callback: any) => {
                setPleaseWaitPopup(true);
                try {
                    await axios.delete(`${baseURL}vehicleFolder?id=${folderId}&userId=${auth?.id}`, {
                        headers: {
                            'X-Tenant-ID': tenantId,
                        },
                        withCredentials: true,
                    });

                    if (folderId === selectedFolderId) {
                        setSelectedFolderId(null);
                        setTableData([]);
                    }

                    fetchFolders();
                    handleTenantError(null);
                } catch (err) {
                    handleTenantError(err);
                } finally {
                    setPleaseWaitPopup(false);
                    callback();
                }
            },
        });

        setDeletePopup(true);
    };

    const handleDelete = (row: VehicleChecklistRow) => {
        setData({
            title: "Delete file",
            summary: "Are you sure you want to delete this file?",
            confirmBtn: "Yes",
            isDelete: (callback: any) => {
                setPleaseWaitPopup(true);

                fetch(`${baseURL}vehicleFiles?id=${row.id}&userId=${auth?.id}`, {
                    method: "DELETE",
                    headers: {
                        'X-Tenant-ID': tenantId,
                    }
                })
                    .then(() => {
                        setPleaseWaitPopup(false);
                        setIsFileRefresh((prev) => !prev);
                        handleTenantError(null);
                    })
                    .catch((error) => {
                        setPleaseWaitPopup(false);
                        handleTenantError(error);
                    });

                callback();
            },
        });

        setDeletePopup(true);
    };


    return (
        <>
            <div className="w-full mt-2 flex gap-4">
                <div className="w-1/4 border rounded-lg p-3">
                    <h3 className="font-semibold text-md">Folders</h3>
                    <div className="flex gap-2 mt-2 mb-3">
                        <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                setIsEditMode(false);
                                setNewFolderName("");
                                setOpenFolderDialog(true);
                            }}
                        >
                            Add
                        </Button>
                        <Button
                            size="small"
                            startIcon={<EditIcon />}
                            disabled={!selectedFolderId}
                            onClick={() => {
                                const folderToEdit = folders.find(f => f.id === selectedFolderId);
                                if (folderToEdit) {
                                    setEditFolderId(folderToEdit.id);
                                    setEditFolderName(folderToEdit.folderName);
                                    setEditFolderIsLocked(folderToEdit.isLocked);
                                    setIsEditMode(true);
                                    setOpenFolderDialog(true);
                                }
                            }}
                        >
                            Edit
                        </Button>
                        <Button
                            size="small"
                            startIcon={<DeleteIcon />}
                            disabled={!selectedFolderId || folders.find(f => f.id === selectedFolderId)?.isLocked}
                            color="error"
                            onClick={() => {
                                if (selectedFolderId) deleteFolder(selectedFolderId);
                            }}
                        >
                            Delete
                        </Button>
                    </div>

                    {folders?.map((folder) => (
                        <div
                            key={folder.id}
                            className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer mb-1 ${selectedFolderId === folder.id ? "bg-blue-100" : "hover:bg-gray-100"
                                }`}
                            onClick={() => {
                                setSelectedFolderId(folder.id);
                                setIsFileRefresh(true);
                            }}
                        >
                            <FolderIcon className="text-gray-600" fontSize="small" />
                            <span className="flex-1 truncate">{folder.folderName}</span>
                            {folder.isLocked && <LockIcon className="text-gray-500" fontSize="small" />}
                        </div>
                    ))}
                </div>

                <div className="w-3/4">
                    <div className={`w-full pb-4`}>
                        <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                            <div className="flex items-center gap-1.5">

                            </div>
                            <div className="flex items-center gap-1.5">
                                <Button
                                    component="label"
                                    variant="contained"
                                    htmlFor={folders?.length <= 0 ? "" : "file-upload"}
                                    className={`px-[8px] py-[6px] ${colorConfigs.btn.main.tailwind} rounded-lg border ${themeStyles.main.borderColor} text-white flex items-center gap-1.5`}
                                    onClick={() => {
                                        if (folders?.length <= 0) {
                                            setConfirmData({
                                                title: 'Warning',
                                                summary: `To upload files, please create a folder first.`,
                                                confirmBtn: 'Ok',
                                                isConfirm: (callback: any) => {
                                                    callback();
                                                }
                                            });
                                            setConfirmPopup(true);
                                        }
                                    }}
                                >
                                    <span className="text-xs font-medium">Upload</span>
                                    <VisuallyHiddenInput
                                        id="file-upload"
                                        type="file"
                                        accept=".png, .jpg, .jpeg, .pdf"
                                        onChange={handleUploadNewFile}
                                    />
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className={`w-full border ${themeStyles.main.borderColor} rounded-[16px]`}>
                        <div className={`flex justify-between items-center p-4 border-b ${themeStyles.main.borderColor}`}>
                            <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Files</h2>
                        </div>

                        <CustomDataGrid
                            filteredRows={tableData}
                            columns={columns?.map(col => ({
                                ...col,
                                headerClassName: themeStyles.table.headerBg,
                            }))}
                            loading={loading}
                            totalLength={totalLength}
                            paginationModel={paginationModel}
                            setPaginationModel={setPaginationModel}
                        />
                    </div>
                </div>
            </div>

            <Dialog open={openFolderDialog}
                onClose={() => setOpenFolderDialog(false)}
                maxWidth="xs"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '16px',
                        padding: '10px',
                    }
                }}>
                <DialogTitle>
                    <div
                        style={{
                            height: "50px",
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",

                        }}
                    >
                        {isEditMode ? "Edit Folder" : "Add Folder"}
                        <CloseRoundedIcon
                            className={`text-xl mr-4 cursor-pointer ${themeStyles.main.textHeadingColor}`}
                            onClick={() => {
                                setOpenFolderDialog(false);
                            }}
                        />
                    </div>
                </DialogTitle>
                <DialogContent>
                    <div className="space-y-4">
                        <div>
                            <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Folder Name</label>
                            <input
                                type="text"
                                className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                value={isEditMode ? editFolderName : newFolderName}
                                onChange={(e) => {
                                    isEditMode
                                        ? setEditFolderName(e.target.value)
                                        : setNewFolderName(e.target.value);
                                }}
                                required
                            />
                        </div>
                    </div>
                    <div className="space-y-4 mt-4">
                        <div>
                            <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Locked?</label>
                            <RadioGroup
                                row
                                value={(isEditMode ? editFolderIsLocked : newFolderIsLocked) ? "Yes" : "No"}
                                onChange={(e) => {
                                    const value = e.target.value === "Yes";
                                    isEditMode ? setEditFolderIsLocked(value) : setNewFolderIsLocked(value);
                                }}
                            >
                                <FormControlLabel
                                    value="Yes"
                                    control={
                                        <Radio
                                            sx={{
                                                color: "gray",
                                                "&.Mui-checked": {
                                                    color: "#167AC3",
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: 14,
                                                },
                                            }}
                                        />
                                    }
                                    label="Yes"
                                    sx={{
                                        color: themeStyles.form.labelTextColor
                                    }}
                                />

                                <FormControlLabel
                                    value="No"
                                    control={
                                        <Radio
                                            sx={{
                                                color: "gray",
                                                "&.Mui-checked": {
                                                    color: "#167AC3",
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: 14,
                                                },
                                            }}
                                        />
                                    }
                                    label="No"
                                    sx={{
                                        color: themeStyles.form.labelTextColor
                                    }}
                                />
                            </RadioGroup>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={async () => {
                            if (isEditMode) {
                                if (editFolderId) await updateFolder(editFolderId);
                            } else {
                                await addFolder();
                            }
                            setOpenFolderDialog(false);
                        }}
                        disabled={
                            isEditMode ? !editFolderName.trim() : !newFolderName.trim()
                        }
                        sx={{
                            backgroundColor: colorConfigs.btn.main.bg
                        }}
                        variant="contained"
                    >
                        Save
                    </Button>

                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: colorConfigs.btn.cancel.bg,
                            border: colorConfigs.btn.cancel.border,
                            color: colorConfigs.btn.cancel.color
                        }}
                        onClick={() => setOpenFolderDialog(false)}
                    >
                        Cancel
                    </Button>

                </DialogActions>
            </Dialog>

            <SendEmailVehicleFile
                fileNameForEmail={fileNameForEmail}
                openAddSendEmailVehicleFile={openSendEmailPopup}
                setOpenAddSendEmailVehicleFile={setOpenSendEmailPopup}
                setRefreshSendEmailVehicleFile={setIsFileRefresh}
            />

            <FilesPreviewPopup
                showPopup={imagePopup}
                setShowPopup={setImagePopup}
                fileName={previewFilePath}
                setFileName={setPreviewFilePath}
                vehicleId={idNumber.state}
                folderId={selectedFolderId ?? undefined}
                fileSize={selectedRow?.fileSize ?? 0}
                uploadedDate={selectedRow?.uploadedDate ?? ""}
                vehicleFilesId={selectedRow?.id ?? 0}
                tags={selectedRow?.tags ?? ""}
                description={selectedRow?.description ?? ""}
                createdAt={selectedRow?.createdAt ?? ""}
                createdBy={selectedRow?.createdBy ?? ""}
                onUploadSuccess={() => setIsFileRefresh(prev => !prev)}
            />

            <PleaseWaitPopup pleaseWaitPopup={pleaseWaitPopup} />
        </>
    );

};